import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useRouterStore } from 'apps/hooks';
import { useViewerWidth } from 'libs/rendering/hooks';

import Loading from 'components/Loading';
import RightClickMenu from 'components/RightClickMenu/RightClickMenu';

import { NavCube } from 'libs/rendering/gltf/components/navCube';
import { ReturnButton } from 'libs/rendering/gltf/components/returnButton';

import {
  CadViewerStyled,
  PreviewCadViewerStyled,
} from 'libs/rendering/gltf/viewer.styled';

import {
  Canvas3DStyled,
  RowWrapper,
  LegendWrapper,
  UnitsWrapper,
  GradientWrapper,
  Gradient,
  BinaryGradient,
  NodeStyled,
} from './canvas3D.styled';

const gestureType = {
  ZOOM: 'zoom',
  PAN: 'pan',
  UNKNOWN: 'unknown',
};

function dist(touchEv) {
  /* The distance between two simultaneous touches */
  return Math.hypot(
    touchEv[0].pageX - touchEv[1].pageX,
    touchEv[0].pageY - touchEv[1].pageY,
  );
}

function vectorAngle(ev1, ev2) {
  /* The direction of travel of a single touch between two events */
  const vectFingerOne = [
    ev2[0].pageX - ev1[0].pageX,
    ev2[0].pageY - ev1[0].pageY,
  ];
  const vectFingerTwo = [
    ev2[1].pageX - ev1[1].pageX,
    ev2[1].pageY - ev1[1].pageY,
  ];
  const magVectFOne = Math.sqrt(vectFingerOne[0] ** 2 + vectFingerOne[1] ** 2);
  const magVectFTwo = Math.sqrt(vectFingerTwo[0] ** 2 + vectFingerTwo[1] ** 2);
  const dotProduct =
    vectFingerOne[0] * vectFingerTwo[0] + vectFingerOne[1] * vectFingerTwo[1];
  if (magVectFOne === 0 || magVectFTwo === 0) {
    return null;
  }
  return Math.acos(dotProduct / (magVectFOne * magVectFTwo));
}

// Issues

// When removing a finger from Pan/Zoom should 1 finger touch to persist?
// This can be irritating with panning in particular

class ZoomState {
  gesturing = false;
  gesture = gestureType.UNKNOWN;
  prev = [];
  curr = [];

  GestureStart = (e, viewer) => {
    this.viewer = viewer;
    this.gesturing = true;
    this.rect = e.target.getBoundingClientRect();
    this.setTouch('prev', e);
    this.setTouch('curr', e);
  };

  setTouch(attr, e) {
    this[attr] = [
      {
        pageX: e.touches[0].pageX,
        pageY: e.touches[0].pageY,
      },
      {
        pageX: e.touches[1].pageX,
        pageY: e.touches[1].pageY,
      },
    ];
  }

  GestureMove = e => {
    switch (this.gesture) {
      case gestureType.ZOOM:
        this.prev = this.curr;
        this.setTouch('curr', e);
        this.Zoom();
        break;
      case gestureType.PAN:
        this.prev = this.curr;
        this.setTouch('curr', e);
        this.Pan();
        break;
      default:
        this.setTouch('curr', e);
        this.Classify();
    }
  };

  Classify = () => {
    /* Once there are two fingers down we should classify as soon as we have sufficient movement.
    If there is a sufficient move of both touchEv the same vector then classify as Pan

    Alternate More Efficient Implementation of Gesture Classification (though Vector Angle seems to work fine)


    const dPrev = Math.abs(this.prev[0].pageX - this.prev[1].pageX) + Math.abs(this.prev[0].pageY - this.prev[1].pageY); // distance btw fingers at start
    const dCurr =  Math.abs(this.curr[0].pageX - this.curr[1].pageX) + Math.abs(this.curr[0].pageY - this.curr[1].pageY); // distance btw fingers after move
    const zoomAmount = abs(dPrev - dCurr);
    if (zoomAmount > zoomThreshold) {
      this.Zoom()
    } else {
      const dragX =  Math.abs(this.curr[0].pageX - this.prev[0].pageX);
      const dragY = Math.abs(this.curr[0].pageY - this.prev[0].pageY);
      if abs(dragX) > DragThreshold) || abs(dragY) > DragThreshold {
        this.Pan()
      }
    }
    */

    const taxicabDist =
      Math.abs(this.curr[0].pageX - this.prev[0].pageX) +
      Math.abs(this.curr[0].pageY - this.prev[0].pageY) +
      Math.abs(this.curr[1].pageX - this.prev[1].pageX) +
      Math.abs(this.curr[1].pageY - this.prev[1].pageY);

    if (taxicabDist > 20) {
      const radians = vectorAngle(this.prev, this.curr);
      if (radians === null) {
        return;
      }
      if (radians > Math.PI / 3) {
        this.gesture = gestureType.ZOOM;
        this.Zoom();
      } else {
        this.gesture = gestureType.PAN;
        this.viewer.settings.isLeftClickOrbit = false;
        this.viewer.window.SetCursor(
          this.curr[0].pageX - this.rect.left,
          this.curr[0].pageY - this.rect.top,
        );
        this.viewer.window.MouseDown();
        this.Pan();
      }
    }
  };

  Zoom = () => {
    if (dist(this.prev) > dist(this.curr)) {
      this.viewer.window.MouseScroll(-1); // zoom out
    } else if (dist(this.prev) < dist(this.curr)) {
      this.viewer.window.MouseScroll(1); // zoom in
    }
  };

  Pan = () => {
    this.viewer.window.SetCursor(
      this.curr[0].pageX - this.rect.left,
      this.curr[0].pageY - this.rect.top,
    );
    this.viewer.window.MouseMove();
  };

  GestureEnd = () => {
    if (this.gesture === gestureType.PAN) {
      this.viewer.settings.isLeftClickOrbit = true;
      this.viewer.window.MouseUp();
    }
    this.gesturing = false;
    this.gesture = gestureType.UNKNOWN;
    this.prev = [];
    this.curr = [];
  };
}

const legendSteps = 9;

const Canvas3D = observer(function _Canvas3D() {
  const viewerControl = useViewerControl();

  const routerStore = useRouterStore();

  const { viewer, file, isPreview } = viewerControl;
  const [zoom] = useState(() => new ZoomState());

  const settings = viewer?.settings;
  const currCamera = settings?.CurrentCamera();

  const mousePointing =
    viewer &&
    (viewer.selectionManager?.HighlightCount() > 0 ||
      viewer.session?.IsSomethingHover());

  /*
    Gradient mesh field
  */

  const currTessFieldType = currCamera?.GetCurrTessField();

  const fieldModeEnum = viewer?.module.TessFieldType;
  const runningField =
    fieldModeEnum && settings?.GetRunningTest() !== fieldModeEnum.TessFieldNone;

  const runningFieldEvol = runningField ? settings?.GetRunningTestPerc() : -1;
  const spinnerText = runningFieldEvol
    ? runningFieldEvol === 0
      ? `Preparing analysis...`
      : runningFieldEvol > 99
      ? `Postprocessing...`
      : `Calculating: ${runningFieldEvol.toFixed(1)}%`
    : '';

  const thereIsTessField =
    currCamera && currTessFieldType !== fieldModeEnum.TessFieldNone
      ? currCamera.HasTessField(currTessFieldType)
      : false;
  const gradientVisible = currCamera?.IsInstance() && thereIsTessField;

  const currGradient = currTessFieldType
    ? currCamera?.GetFieldGradient(currTessFieldType)
    : null;

  const getGradientMin = () => {
    if (currGradient) {
      if (
        currTessFieldType === fieldModeEnum.TessFieldOverHang ||
        currTessFieldType === fieldModeEnum.TessFieldSharpEdges
      ) {
        // degs
        return (180.0 * currGradient.min) / Math.PI;
      }
      return currGradient.min;
    }
    return null;
  };

  const getGradientMax = () => {
    if (currGradient) {
      if (
        currTessFieldType === fieldModeEnum.TessFieldOverHang ||
        currTessFieldType === fieldModeEnum.TessFieldSharpEdges
      ) {
        // degs
        return (180.0 * currGradient.max) / Math.PI;
      }
      return currGradient.max;
    }
    return null;
  };

  const gradientLegendValues = () => {
    const min = getGradientMin();
    const max = getGradientMax();
    const span = max - min;
    const step = span / parseFloat(legendSteps - 1);

    const values = [];
    const digits =
      currTessFieldType === fieldModeEnum?.TessFieldOverHang ||
      currTessFieldType === fieldModeEnum?.TessFieldSharpEdges
        ? 0
        : 1;

    for (let i = 0; i < legendSteps; i++) {
      let value;
      if (currGradient?.reversed) {
        value = Number((min + i * parseFloat(step)).toFixed(digits));
      } else {
        value = Number((max - i * parseFloat(step)).toFixed(digits));
      }

      values.push(<RowWrapper key={i}>{`${value}`}</RowWrapper>);
    }
    return values;
  };

  const getLegendUnits = () => {
    if (!currTessFieldType) return '';

    return currTessFieldType === fieldModeEnum?.TessFieldThickness
      ? '[mm]'
      : currTessFieldType === fieldModeEnum?.TessFieldOverHang
      ? '[°]'
      : '[°]';
  };

  // - this useEffect takes care of loading a new file into the
  // viewer, it is here so it is part of the global canvas and
  // handles updates to the viewerControl.file
  // - it only reacts when file.id changes or the conversion
  // status changes, so that we load a file into the viewer when
  // it is a different file from the one that was already loaded
  // before, or we load a file if our conversion status changes
  // from PENDING -> SUCCESS
  useEffect(() => {
    if (file?.id !== viewerControl.lastLoadedId) {
      // - we only need to do something if lastLoadedId !== file.id
      // - when status changes PENDING -> SUCCESS, lastLoadedId was
      // not changed by LoadCadFile, so this code still runs
      // - lastLoadedId is set in viewerControl.LoadCadFile once a
      // file was successfully loaded
      if (viewerControl.lastLoadedId) {
        // - if we're here and lastLoadedId was ever set, then we know
        // we're about to load a new file, but we don't know yet if that
        // will be successful or if we need to wait for the conversion,
        // so we hide the viewer
        viewerControl.lastLoadedId = null;
        viewerControl.viewerVisible = false;
      }
      if (file?.conversionGltfjson?.status === 'SUCCESS') {
        // - if the file has a gltf conversion we can load it, but keep
        // in mind that this code and the code above can run in different
        // invocations of this useEffect
        // - this will set lastLoadedId to something other then null
        viewerControl.loadCadFile(file, { isPreview });
      }
    }
  }, [file?.id, file?.conversionGltfjson?.status]);

  const [canvasWidth, rightOffset] = useViewerWidth();

  // - this useEffect takes care of loading a new file into the
  // viewer, it is here so it is part of the global canvas and
  // handles updates to the viewerControl.file
  // - it only reacts when file.id changes or the conversion
  // status changes, so that we load a file into the viewer when
  // it is a different file from the one that was already loaded
  // before, or we load a file if our conversion status changes
  // from PENDING -> SUCCESS
  useEffect(() => {
    viewerControl.viewer?.updateCanvas();
  }, [canvasWidth, file]);

  /*
    event handling
  */

  function checkCanvasInput(e) {
    if (viewer && viewer.isOk) {
      const isCanvas =
        e && e.target && e.target.id && e.target.id === viewerControl.canvasId;
      if (!isCanvas) {
        viewer.selectionManager.ClearHighlight();
        viewer.window.CancelDrag();
      }
      return isCanvas;
    }
    return false;
  }

  function setCursorPosition(e) {
    const dpr = window.devicePixelRatio || 1.0;
    viewer.window.SetCursor(
      e.nativeEvent.offsetX * dpr,
      e.nativeEvent.offsetY * dpr,
    );
  }

  function setTouchPosition(e, callback) {
    const touch = e.touches[0];
    if (checkCanvasInput(e) && touch) {
      const rect = e.target.getBoundingClientRect();
      viewer.window.SetCursor(touch.pageX - rect.left, touch.pageY - rect.top);
      callback();
    }
  }

  function touchStart(e) {
    if (!viewerControl.cadReady) return false;
    if (e.touches.length === 1) {
      setTouchPosition(e, viewer.window.MouseDown);
    } else if (e.touches.length === 2) {
      zoom.GestureStart(e, viewer);
    }
    return false;
  }

  function touchMove(e) {
    if (!viewerControl.cadReady) return false;
    if (zoom.gesturing) {
      zoom.GestureMove(e);
    } else {
      setTouchPosition(e, viewer.window.MouseMove);
    }
    return false;
  }

  function touchEnd(e) {
    if (!viewerControl.cadReady) return false;
    if (zoom.gesturing) {
      zoom.GestureEnd(e);
    }
    setTouchPosition(e, viewer.window.MouseUp);
    if (e.touches.length === 1) {
      // allow the user to remove a finger from a pinch and switch to dragging and dropping
      setTouchPosition(e, viewer.window.MouseDown);
    }
    return false;
  }

  function touchCancel(e) {
    /* OS level event e.g. orientation change - untested */
    if (!viewerControl.cadReady) return;
    if (viewer && viewer.isOk) {
      viewer.selectionManager.ClearHighlight();
      viewer.window.CancelDrag();
    }
    zoom.GestureEnd(e);
  }

  function mouseDown(e) {
    if (!viewerControl.cadReady) return;
    if (checkCanvasInput(e)) {
      if (e.buttons === 1) {
        // left click (camera orbit)
        setCursorPosition(e);
        viewer.window.MouseDown();
      } else if (e.button === 2) {
        // right click...
        if (!viewerControl.contextMenuOpened) {
          // activate pan
          setCursorPosition(e);
          viewer.window.MouseRightDown();
        }
      }
      e.stopPropagation();
    }
  }

  function mouseUp(e) {
    if (!viewerControl.cadReady) return;
    if (checkCanvasInput(e)) {
      if (e.button === 0) {
        // left click (camera orbit)
        setCursorPosition(e);
        viewer.window.MouseUp();
      } else if (e.button === 2) {
        // right click
        if (!viewerControl.contextMenuOpened) {
          setCursorPosition(e);
          viewer.window.MouseRightUp();
        }

        // context menu start?
        const camera = viewerControl.viewer?.settings.CurrentCamera();
        const hasPanned = camera?.HasPanned();
        // panning end...
        viewerControl.contextMenuOpened = false;
        if (!hasPanned) {
          // yes
          const cadItem = viewerControl.hoveredCadItem;
          if (cadItem) {
            viewerControl.contextMenuOpened = true;
          } else {
            const session = viewerControl.viewer?.session;
            if (session && session.IsSomethingHover()) {
              viewerControl.contextMenuOpened = true;
            }
          }
        }
      }
      e.stopPropagation();
    }
  }

  function mouseMove(e) {
    if (!viewerControl.cadReady) return;
    if (checkCanvasInput(e)) {
      setCursorPosition(e);
      viewer.window.MouseMove();
      e.stopPropagation();
    }
  }

  function mouseLeave(e) {
    if (!viewerControl.cadReady) return;
    if (viewer && viewer.isOk) {
      // check if still inside canvas
      //  (some children e.g. pin note has been entered...)
      const canvas = e.currentTarget;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect) return;
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        // still inside canvas...
        return;
      }

      viewer.selectionManager.ClearHighlight();
      viewer.window.CancelDrag();
    }
  }

  function mouseWheel(e) {
    if (!viewerControl.cadReady) return;
    if (checkCanvasInput(e)) {
      // (camera zoom)
      let delta = e.deltaY;
      if (e.ctrlKey) {
        // zoom gesture on a trackpad
        delta = -delta;
      }
      if (delta > 0) {
        viewer.window.MouseScroll(1); // zoom in
      } else if (delta < 0) {
        viewer.window.MouseScroll(-1); // zoom out
      }

      // e.preventDefault(); // don't scroll the page
      // Above line is commented because it throws error
      // "Unable to preventDefault inside passive event listener due to target being treated as passive.""
      e.stopPropagation();
    }
  }

  function keyPress(e) {
    e.preventDefault(); // don't scroll the page if arrow keys
  }

  function onContextMenu(e) {
    // don't show context menu on right click
    e.preventDefault();
  }

  function hoverOnTop(hoverItem) {
    const selManager = viewerControl.viewer.selectionManager;
    selManager?.ClearHighlight();

    if (!hoverItem) {
      viewerControl?.SetHighlightBehaviour(null, null);
      if (viewerControl.contextMenuItem) {
        selManager?.AddHighlightItem(viewerControl.contextMenuItem._wasm);
      }
      selManager.reportWasmChange();
      return;
    }

    selManager?.AddHighlightItem(hoverItem._wasm);
    hoverItem.highlighted = false; // do not affect tree view...
    viewerControl?.SetHighlightBehaviour('on-front', hoverItem);
    selManager.reportWasmChange();
  }

  const contextMenuOptions = () => {
    if (!viewerControl.contextMenuOpened) return [];
    if (viewerControl.isPreview) return [];

    const cadItem = viewerControl.hoveredCadItem;
    let cadOptions = [];

    // TODO (click events pending)
    const userId = viewerControl.hoveredUserCamera;
    if (userId) {
      cadOptions = [
        {
          icon: 'user',
          label: userId,
          isTitle: true,
        },
        {
          icon: 'view', // visibility
          label: 'Hide', // if already hidden, never highlighted...
          closeAfter: true,
        },
        {
          icon: 'camera', // follow user
          label: 'Follow',
          closeAfter: true,
        },
      ];
    } else if (cadItem) {
      if (cadItem.IsAssemblyInstance()) {
        const isFocused = cadItem.uuid === settings?.GetFocusedItem()?.uuid;
        const instanceCount = viewerControl.cadFile?.assembly?.InstanceCount();
        const body = cadItem.GetBody();
        const mesh = body?.GetBodyMesh();
        if (mesh != null && instanceCount > 1) {
          cadOptions = [
            {
              icon: cadItem.visible ? 'view_off' : 'view', // visibility
              onClick: () => {
                cadItem.visible = !cadItem.visible;
                viewerControl.ForceRender();
              },
              label: cadItem.visible ? 'Hide' : 'Show',
            },
            {
              icon: 'expand', // inspect, new tab
              onClick: () => {
                const newpath = routerStore.route.name;
                routerStore.navigate(newpath, {
                  ...routerStore.route.params,
                  partId: body.uuid,
                });
              },
              label: 'Isolate',
              closeAfter: true,
            },
            {
              icon: isFocused ? 'zoom_out' : 'zoom_in', // focus instance in current tab
              onClick: isFocused
                ? () => viewerControl.FocusCadItem(null, true)
                : () => viewerControl.FocusCadItem(cadItem, true),
              label: isFocused ? 'Unfocus' : 'Focus',
              closeAfter: true,
            },
            {
              icon: 'cube', // Extract part and create rfq
              onClick: () => {
                const newpath = routerStore.route.name;
                routerStore.navigate(newpath, {
                  ...routerStore.route.params,
                  panel: 'rfq',
                  partId: body.uuid,
                });
              },
              label: 'Make Part',
              closeAfter: true,
            },
          ];

          const subAssemblyOptions = [];
          const currentSubAssembly = currCamera.GetSubAssembly();

          let parent = cadItem.GetProduct();
          while (parent != null && !parent.IsRoot()) {
            if (parent.IsSubassembly() && parent.ChildCount() > 1) {
              const subAssembly = parent;
              if (
                currentSubAssembly == null ||
                currentSubAssembly.uuid !== subAssembly.uuid
              ) {
                subAssemblyOptions.push({
                  icon: 'expand', // inspect, new sub-assembly tab
                  onClick: () =>
                    viewerControl.InspectCadItem(false, subAssembly),
                  label: subAssembly.name,
                  closeAfter: true,
                  onMouseEnter: () => hoverOnTop(subAssembly),
                  onMouseLeave: () => hoverOnTop(null),
                });
              }
            }
            parent = parent.GetParent();
          }
          if (subAssemblyOptions.length > 0) {
            cadOptions.push({
              icon: 'folder',
              label: 'sub-assemblies',
              options: subAssemblyOptions,
              onMouseEnter: () => hoverOnTop(null),
              onMouseLeave: () => hoverOnTop(null),
            });
          }
        }
      }
    }

    if (cadOptions.length === 0) {
      viewerControl.contextMenuOpened = false;
    }
    return cadOptions;
  };

  if (viewerControl?.isPreview) {
    return (
      <>
        <PreviewCadViewerStyled
          id={viewerControl?.wrapperId}
          maxHeight={window.innerHeight}
          maxWidth={window.innerWidth}
          tabIndex='0'
          itemScope
          itemType='http://schema.org/CreativeWork'
        >
          <>
            <Canvas3DStyled
              tabIndex='0'
              id={viewerControl.canvasId}
              width='100%'
              onMouseUp={mouseUp}
              onMouseDown={mouseDown}
              onMouseMove={mouseMove}
              onMouseLeave={mouseLeave}
              onTouchStart={touchStart}
              onTouchEnd={touchEnd}
              onTouchMove={touchMove}
              onTouchCancel={touchCancel}
              onWheel={mouseWheel}
              onKeyDown={keyPress}
              onContextMenu={onContextMenu}
              mousePointing={mousePointing}
              isInAnnotationMode={
                viewer?.selectionManager?.IsSelectionModeLink() || false
              }
              hidden={false}
            />
            <RightClickMenu isDark options={contextMenuOptions()} />
          </>
          <ReturnButton />
        </PreviewCadViewerStyled>
      </>
    );
  }

  if (viewerControl?.viewerFailed || viewerControl?.conversionProblem) {
    return null;
  }

  return (
    <CadViewerStyled
      id={viewerControl?.wrapperId}
      tabIndex='0'
      itemScope
      itemType='http://schema.org/CreativeWork'
    >
      <>
        {runningField && (
          <Loading text={spinnerText} isLayoutCentered visible />
        )}
        <Canvas3DStyled
          tabIndex='0'
          id={viewerControl.canvasId}
          width={canvasWidth}
          onMouseUp={mouseUp}
          onMouseDown={mouseDown}
          onMouseMove={mouseMove}
          onMouseLeave={mouseLeave}
          onTouchStart={touchStart}
          onTouchEnd={touchEnd}
          onTouchMove={touchMove}
          onTouchCancel={touchCancel}
          onWheel={mouseWheel}
          onKeyDown={keyPress}
          onContextMenu={onContextMenu}
          mousePointing={mousePointing}
          isInAnnotationMode={
            viewer?.selectionManager?.IsSelectionModeLink() || false
          }
          hidden={!viewerControl?.viewerVisible}
        />
        <RightClickMenu isDark options={contextMenuOptions()} />
        <NavCube right={rightOffset} />

        <LegendWrapper
          right={rightOffset}
          visible={viewerControl?.viewerVisible && gradientVisible}
        >
          <UnitsWrapper>{getLegendUnits()}</UnitsWrapper>
          <GradientWrapper>
            {currGradient?.binary ? <BinaryGradient /> : <Gradient />}
          </GradientWrapper>
          <NodeStyled>{gradientLegendValues()}</NodeStyled>
        </LegendWrapper>
      </>
    </CadViewerStyled>
  );
});

export default Canvas3D;
